import { apiClient } from 'axios-client';
import env from 'env';
import Keycloak from 'keycloak-js';

const keycloakConfig = {
  url: env.KEYCLOAK_AUTH_URL,
  realm: env.KEYCLOAK_REALM,
  clientId: env.KEYCLOAK_CLIENT_ID,
};

export const keycloak = new Keycloak(keycloakConfig);

export const createKeycloakUser = async (user: {
  email: string;
  password?: { value: string; temporary?: boolean };
  groups?: string[];
}): Promise<{ id: string }> => {
  const response = await apiClient.post(
    `${env.KEYCLOAK_AUTH_URL}/admin/realms/${env.KEYCLOAK_REALM}/users`,
    {
      email: user.email,
      username: user.email,
      emailVerified: true,
      enabled: true,
      credentials: user.password
        ? [{ type: 'password', ...user.password }]
        : undefined,
      groups: user.groups,
    },
  );

  const locationHeader = response.headers['location'];
  const id = locationHeader.split('/').pop();

  if (!id) {
    throw new Error('Could not extract user ID from Location header');
  }

  return { id };
};

export const removeKeycloakUserFromGroup = async (
  userId: string,
  groupId: string,
): Promise<void> => {
  await apiClient.delete(
    `${env.KEYCLOAK_AUTH_URL}/admin/realms/${env.KEYCLOAK_REALM}/users/${userId}/groups/${groupId}`,
  );
};

export const addKeycloakUserToGroup = async (
  userId: string,
  groupId: string,
): Promise<void> => {
  await apiClient.put(
    `${env.KEYCLOAK_AUTH_URL}/admin/realms/${env.KEYCLOAK_REALM}/users/${userId}/groups/${groupId}`,
    {},
  );
};

export const getKeycloakGroupByPath = async (
  path: string,
): Promise<{ id: string; name: string; path: string }> => {
  const { data } = await apiClient.get(
    `${env.KEYCLOAK_AUTH_URL}/admin/realms/${env.KEYCLOAK_REALM}/group-by-path/${path}`,
  );

  return { id: data.id, name: data.name, path: data.path };
};

export const updateKeycloakUser = async (
  userId: string,
  user: {
    email?: string;
    password?: { value: string; temporary?: boolean };
  },
): Promise<void> => {
  await apiClient.put(
    `${env.KEYCLOAK_AUTH_URL}/admin/realms/${env.KEYCLOAK_REALM}/users/${userId}`,
    {
      email: user.email,
      username: user.email,
      credentials: user.password
        ? [{ type: 'password', ...user.password }]
        : undefined,
    },
  );
};

export const findKeycloakUsers = async (query?: {
  email?: string;
  limit?: number;
  exact?: boolean;
  first?: number;
}): Promise<Array<{ id: string; email: string }>> => {
  const { data: users } = await apiClient.get(
    `${env.KEYCLOAK_AUTH_URL}/admin/realms/${env.KEYCLOAK_REALM}/users`,
    {
      params: {
        email: query?.email,
        max: query?.limit,
        exact: query?.exact,
        first: query?.first,
      },
    },
  );

  return users.map((user) => ({ id: user.id, email: user.email }));
};

export const getKeycloakUser = async (
  userId: string,
): Promise<{ id: string; email: string }> => {
  const { data: user } = await apiClient.get(
    `${env.KEYCLOAK_AUTH_URL}/admin/realms/${env.KEYCLOAK_REALM}/users/${userId}`,
  );

  return { id: user.id, email: user.email };
};

export const getUserCredentials = async (userId: string): Promise<any[]> => {
  const response = await apiClient.get(
    `${env.KEYCLOAK_AUTH_URL}/admin/realms/${env.KEYCLOAK_REALM}/users/${userId}/credentials`,
  );
  return response.data;
};

export const getUserFederatedIdentity = async (
  userId: string,
): Promise<any[]> => {
  const response = await apiClient.get(
    `${env.KEYCLOAK_AUTH_URL}/admin/realms/${env.KEYCLOAK_REALM}/users/${userId}/federated-identity`,
  );
  return response.data;
};
